import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number | string | null | undefined, currencySymbol?: string): string {
  // 如果没有传入货币符号，默认使用人民币符号
  const symbol = currencySymbol || '¥'
  
  if (amount === null || amount === undefined) {
    return `${symbol}0.00`
  }
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(num)) {
    return `${symbol}0.00`
  }
  
  // 金额截断到2位小数（正确处理负数）
  const truncated = num >= 0 
    ? Math.floor(num * 100) / 100 
    : Math.ceil(num * 100) / 100
  return `${symbol}${truncated.toFixed(2)}`
}

/**
 * 格式化价格显示（截断到4位小数）
 * @param price 价格数值
 * @param currencySymbol 货币符号
 * @returns 格式化的价格字符串
 */
export function formatPrice(price: number | string | null | undefined, currencySymbol?: string): string {
  const symbol = currencySymbol || '¥'
  
  if (price === null || price === undefined) {
    return `${symbol}0.0000`
  }
  const num = typeof price === 'string' ? parseFloat(price) : price
  if (isNaN(num)) {
    return `${symbol}0.0000`
  }
  
  // 价格截断到4位小数
  const truncated = Math.floor(num * 10000) / 10000
  return `${symbol}${truncated.toFixed(4)}`
}

/**
 * 格式化数量显示（截断到4位小数）
 * @param quantity 数量数值
 * @returns 格式化的数量字符串
 */
export function formatQuantity(quantity: number | string | null | undefined): string {
  if (quantity === null || quantity === undefined) {
    return '0.0000'
  }
  const num = typeof quantity === 'string' ? parseFloat(quantity) : quantity
  if (isNaN(num)) {
    return '0.0000'
  }
  
  // 数量截断到4位小数
  const truncated = Math.floor(num * 10000) / 10000
  return truncated.toFixed(4)
}

export function formatNumber(num: number | null | undefined, decimals?: number): string {
  if (num === null || num === undefined || isNaN(num)) {
    return '0'
  }
  
  // 如果指定了小数位数，则按指定格式显示
  if (decimals !== undefined) {
    return num.toLocaleString('zh-CN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  }
  
  // 否则显示原始精度
  if (num === Math.floor(num)) {
    return num.toLocaleString('zh-CN')
  }
  
  return num.toLocaleString('zh-CN')
}

/**
 * 格式化百分比显示
 * @param num 百分比值 (例如: 10 表示 10%)
 * @param decimals 小数位数
 * @returns 格式化的百分比字符串 (例如: "10.00%")
 * 
 * 注意：输入的是已经乘以100的百分比值
 */
export function formatPercent(num: number, decimals: number = 2): string {
  const power = Math.pow(10, decimals)
  const rounded = Math.round(num * power) / power
  return `${rounded.toFixed(decimals)}%`
}

export function getReturnColor(value: number): string {
  if (value > 0) return 'text-green-600'
  if (value < 0) return 'text-red-600'
  return 'text-gray-600'
}

/**
 * 格式化日期显示（只显示日期部分）
 * @param dateString 日期字符串或时间戳字符串
 * @returns 格式化的日期字符串
 */
export function formatDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return '-'
  
  try {
    let date: Date
    
    if (typeof dateString === 'string') {
      // 如果是纯数字字符串（时间戳），转换为数字
      if (/^\d+$/.test(dateString)) {
        date = new Date(parseInt(dateString, 10))
      } else {
        date = new Date(dateString)
      }
    } else {
      date = dateString
    }
    
    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return '-'
    }
    
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  } catch {
    return '-'
  }
}

/**
 * 格式化日期时间显示（显示日期和时间）
 * @param dateString 日期时间字符串或时间戳字符串
 * @returns 格式化的日期时间字符串
 */
export function formatDateTime(dateString: string | Date | null | undefined): string {
  if (!dateString) return '-'
  
  try {
    let date: Date
    
    if (typeof dateString === 'string') {
      // 如果是纯数字字符串（时间戳），转换为数字
      if (/^\d+$/.test(dateString)) {
        date = new Date(parseInt(dateString, 10))
      } else {
        date = new Date(dateString)
      }
    } else {
      date = dateString
    }
    
    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return '-'
    }
    
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
  } catch {
    return '-'
  }
}

export function getReturnIcon(value: number): string {
  if (value > 0) return '↗'
  if (value < 0) return '↘'
  return '→'
}

/**
 * 隐藏用户姓名，只显示姓氏和星号
 * @param name 完整姓名
 * @returns 隐藏后的姓名（如：邓**）
 */
export function maskUserName(name: string): string {
  if (!name || name.length === 0) return '**'
  
  // 获取第一个字符作为姓氏
  const surname = name.charAt(0)
  
  // 根据名字长度决定星号数量
  const nameLength = name.length
  const asterisks = nameLength > 1 ? '*'.repeat(nameLength - 1) : '*'
  
  return surname + asterisks
}
